import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner } from '@cowoo/shared/ui-web';
import { ApiProblem, formatBlock, qk, todayBerlin } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog, Field, Segmented, fieldClass } from '../../ui';
import { toast } from '../../ui/toast';
import { memberName } from '../members/memberRow';

/**
 * "Buchung anlegen" (10-web-admin § 9.3, `AdminDialogsPlans`).
 *
 * A manual booking is free and looks to the member like one they made
 * themselves — which is why the dialog offers only what is **free** on the
 * day: the desks of one published plan, or a room's open blocks. Whatever the
 * server refuses (`desk_taken`, `booking_limit_reached`, `location_not_open`)
 * is shown as its own line; there is no override in v1.
 */
export function NewBookingDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [userId, setUserId] = useState('');
  const [kind, setKind] = useState<'desk' | 'room'>('desk');
  const [day, setDay] = useState(todayBerlin());
  const [planId, setPlanId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [blockStart, setBlockStart] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const members = useQuery({
    queryKey: qk.admin.area('members', { q, picker: true }),
    enabled: q.trim().length >= 2,
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/members', {
        params: { query: { q: q.trim(), limit: 8 } },
      });
      if (error) throw error;
      return data.items;
    },
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });
  const open = (locations.data ?? []).filter((location) => location.status === 'open');

  /** Every published plan of every open house, so "Grundriss" is one select. */
  const plans = useQuery({
    queryKey: ['admin', 'published-plans', open.map((location) => location.id).join(',')],
    enabled: open.length > 0,
    queryFn: async () => {
      const all = await Promise.all(
        open.map(async (location) => {
          const { data, error } = await api.GET('/locations/{id}/plan', {
            params: { path: { id: location.id } },
          });
          if (error) throw error;
          return data.plans.map((plan) => ({
            ...plan,
            locationId: location.id,
            locationName: location.name,
          }));
        }),
      );
      return all.flat();
    },
  });
  const plan = plans.data?.find((item) => item.floorPlanId === planId) ?? plans.data?.[0] ?? null;
  const locationId = plan?.locationId ?? '';

  const desks = useQuery({
    queryKey: ['admin', 'availability', locationId, day],
    enabled: kind === 'desk' && locationId !== '' && day !== '',
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/availability', {
        params: { path: { id: locationId }, query: { day } },
      });
      if (error) throw error;
      return 'desks' in data ? data.desks : [];
    },
  });

  const rooms = useQuery({
    queryKey: ['admin', 'rooms-availability', locationId, day],
    enabled: kind === 'room' && locationId !== '' && day !== '',
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/rooms/availability', {
        params: { path: { id: locationId }, query: { day } },
      });
      if (error) throw error;
      return data.rooms;
    },
  });

  // Only this plan's desks, and only the free ones (§ 9.3 "free only").
  const onPlan = new Set((plan?.placements ?? []).map((placement) => placement.resourceId));
  const freeDesks = (desks.data ?? []).filter((desk) => desk.state === 'free' && onPlan.has(desk.resourceId));
  const planRooms = (rooms.data ?? []).filter((room) => onPlan.has(room.resourceId));
  const room = planRooms.find((item) => item.resourceId === resourceId) ?? null;
  const freeBlocks = (room?.blocks ?? []).filter((block) => block.state === 'free');

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/admin/bookings', {
        body: {
          userId,
          resourceId,
          day,
          ...(kind === 'room' && blockStart !== null ? { blockStart } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast(t('admin.bookings.created'), { tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('bookings') });
      onClose();
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const picked = members.data?.find((member) => member.id === userId) ?? null;
  const ready = userId !== '' && resourceId !== '' && day !== '' && (kind === 'desk' || blockStart !== null);

  return (
    <Dialog
      title={t('admin.bookings.newTitle')}
      confirmLabel={t('admin.bookings.create')}
      onConfirm={() => create.mutate()}
      onClose={onClose}
      busy={create.isPending}
      disabled={!ready}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      <Field label={t('admin.bookings.newMember')}>
        {picked ? (
          <div className="border-line rounded-input flex h-10 items-center justify-between gap-2 border px-3">
            <span className="text-adminBody text-ink font-semibold">
              {memberName(picked)} · {picked.email}
            </span>
            <button
              type="button"
              className="text-muted text-sm font-semibold"
              onClick={() => {
                setUserId('');
                setQ('');
              }}
            >
              {t('common.change')}
            </button>
          </div>
        ) : (
          <>
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={t('admin.bookings.newMemberPlaceholder')}
              className={fieldClass}
            />
            {q.trim().length >= 2 ? (
              <ul className="border-line rounded-input mt-1 max-h-[180px] overflow-y-auto border bg-white">
                {(members.data ?? []).length === 0 ? (
                  <li className="text-adminSmall text-muted px-3 py-2">
                    {t('admin.bookings.newMemberNone')}
                  </li>
                ) : (
                  (members.data ?? []).map((member) => (
                    <li key={member.id}>
                      <button
                        type="button"
                        onClick={() => setUserId(member.id)}
                        className="hover:bg-fog text-adminBody text-ink flex w-full flex-col items-start px-3 py-2 text-left"
                      >
                        <span className="font-semibold">{memberName(member)}</span>
                        <span className="text-muted text-xs">{member.email}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </>
        )}
      </Field>

      <Field label={t('admin.bookings.newKind')}>
        <Segmented
          label={t('admin.bookings.newKind')}
          value={kind}
          onChange={(next) => {
            setKind(next);
            setResourceId('');
            setBlockStart(null);
          }}
          options={[
            { value: 'desk', label: t('admin.bookings.kind.desk') },
            { value: 'room', label: t('admin.bookings.kind.room') },
          ]}
        />
      </Field>

      <div className="flex gap-2.5">
        <div className="flex-1">
          <Field label={t('admin.bookings.newDay')}>
            <input
              type="date"
              value={day}
              min={todayBerlin()}
              onChange={(event) => {
                setDay(event.target.value);
                setResourceId('');
                setBlockStart(null);
              }}
              className={fieldClass}
            />
          </Field>
        </div>
        <div className="flex-1">
          <Field label={t('admin.bookings.newPlan')}>
            <select
              value={plan?.floorPlanId ?? ''}
              onChange={(event) => {
                setPlanId(event.target.value);
                setResourceId('');
                setBlockStart(null);
              }}
              className={fieldClass}
            >
              {(plans.data ?? []).map((item) => (
                <option key={item.floorPlanId} value={item.floorPlanId}>
                  {item.locationName} · {item.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      {kind === 'desk' ? (
        <Field label={t('admin.bookings.newResource')}>
          {freeDesks.length === 0 && desks.isFetched ? (
            <p className="text-adminSmall text-muted">{t('admin.bookings.newNoFree')}</p>
          ) : (
            <select
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
              className={fieldClass}
            >
              <option value="">—</option>
              {freeDesks.map((desk) => (
                <option key={desk.resourceId} value={desk.resourceId}>
                  {[t('admin.bookings.desk', { label: desk.label }), desk.zone].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : (
        <div className="flex gap-2.5">
          <div className="flex-1">
            <Field label={t('admin.bookings.newRoom')}>
              <select
                value={resourceId}
                onChange={(event) => {
                  setResourceId(event.target.value);
                  setBlockStart(null);
                }}
                className={fieldClass}
              >
                <option value="">—</option>
                {planRooms.map((item) => (
                  <option key={item.resourceId} value={item.resourceId}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="flex-1">
            <Field label={t('admin.bookings.newBlock')}>
              {room && freeBlocks.length === 0 ? (
                <p className="text-adminSmall text-muted">{t('admin.bookings.newNoFree')}</p>
              ) : (
                <select
                  value={blockStart === null ? '' : String(blockStart)}
                  onChange={(event) =>
                    setBlockStart(event.target.value === '' ? null : Number(event.target.value))
                  }
                  disabled={room === null}
                  className={fieldClass}
                >
                  <option value="">—</option>
                  {freeBlocks.map((block) => (
                    <option key={block.start} value={block.start}>
                      {formatBlock(block.start)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        </div>
      )}

      <Field label={t('admin.bookings.newNote')}>
        <input value={note} onChange={(event) => setNote(event.target.value)} className={fieldClass} />
      </Field>

      <p className="text-adminSmall text-muted">{t('admin.bookings.newHint')}</p>
    </Dialog>
  );
}
